import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LinkButton } from '../../common/LinkButton/LinkButton'
import { useBillingFiltersStore } from '../../../features/BillingPayments/stores/useBillingFiltersStore/useBillingFiltersStore'
import { FilterToggleButton } from '@/components/common/FilterToggleButton/FilterToggleButton'
import { useActiveFiltersCount } from '@/hooks/useActiveFilterCount/useActiveFilterCount'
import { DEFAULT_BILLING_FILTERING } from '@/utils/constants'
import { ActiveFiltersCounter } from '@/components/common/ActiveFiltersCounter/ActiveFiltersCounter'
import {
    FiltersStoreState,
    createSelectHasDefaultFilters,
} from '@/stores/createFiltersStore'
import { TimelineFilterValue } from '@/components/common/Filter/types'
import { ReportFiltersValues } from '@/features/Analytics/stores/useAnalyticsFiltersStore/types'
import { CommodityFilter } from '@/components/core/filters/CommodityFilter'
import { ProviderFilter } from '@/components/core/filters/ProviderFilter'
import { NodeFilter } from '@/components/core/filters/NodeFilter'
import { ContractFilter } from '@/components/core/filters/ContractFilter'
import { AccountFilter } from '@/components/core/filters/AccountFilter'
import { TagFilter } from '@/components/core/filters/TagFilter'
import { FacilityFilter } from '@/components/core/filters/FacilityFilter'
import { TimelineFilter } from '@/components/core/filters/TimelineFilter'
import { USNFilter } from '@/components/core/filters/USNFilter'
import { InvoiceNoFilter } from '@/components/core/filters/InvoiceNoFilter'
import { useBillingFiltersGetParams } from '@/features/BillingPayments/hooks/useBillingFiltersGetParams'

export type BillingFiltersContainerProps = {
    activeFilters: ReportFiltersValues
    onChange: FiltersStoreState<ReportFiltersValues>['updateActiveFilter']
    showResetFiltersButton?: boolean
    handleReset?: () => void
}

export const BillingFiltersContainer: React.FC<
    BillingFiltersContainerProps
> = ({ activeFilters, onChange, showResetFiltersButton, handleReset }) => {
    const { t } = useTranslation('translations')
    const containerRef = useRef<HTMLDivElement>(null)
    const [isVisible, setIsVisible] = useState(true)

    const activeFiltersCount = useActiveFiltersCount(
        activeFilters,
        DEFAULT_BILLING_FILTERING
    )

    const handleToggleVisibility = () => {
        setIsVisible((prevVisible) => !prevVisible)
    }

    const params = useBillingFiltersGetParams(activeFilters)

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap gap-4" ref={containerRef}>
                <div
                    className="flex items-center justify-between gap-2"
                    data-testid="analytics-filters-title-row"
                >
                    <div className="text-heading-6 py-2.5">
                        {t('filters.title')}
                    </div>
                    {!isVisible && (
                        <ActiveFiltersCounter count={activeFiltersCount} />
                    )}
                </div>
                {isVisible && (
                    <>
                        <NodeFilter
                            name="billing-node-filter"
                            value={activeFilters.node.map((id) =>
                                id.toString()
                            )}
                            defaultValue={DEFAULT_BILLING_FILTERING.node.map(
                                (id) => id.toString()
                            )}
                            containerRef={containerRef}
                            onChange={(value: string[]) => {
                                onChange(
                                    'node',
                                    value.map((id) => parseInt(id))
                                )
                            }}
                        />
                        <CommodityFilter
                            name="billing-commodity-filter"
                            containerRef={containerRef}
                            value={activeFilters.commodity.map((id) =>
                                id.toString()
                            )}
                            defaultValue={DEFAULT_BILLING_FILTERING.commodity.map(
                                (id) => id.toString()
                            )}
                            onChange={(value: string[]) => {
                                onChange(
                                    'commodity',
                                    value.map((id) => parseInt(id))
                                )
                            }}
                            params={{
                                'nodeIds[]': params['nodeIds[]'],
                            }}
                        />
                        <ProviderFilter
                            name="billing-provider-filter"
                            containerRef={containerRef}
                            value={(activeFilters.provider ?? []).map((id) =>
                                id.toString()
                            )}
                            defaultValue={(
                                DEFAULT_BILLING_FILTERING.provider ?? []
                            ).map((id) => id.toString())}
                            onChange={(value: string[]) => {
                                onChange(
                                    'provider',
                                    value.map((id) => parseInt(id))
                                )
                            }}
                            params={{
                                'nodeIds[]': params['nodeIds[]'],
                                'commodityTypeIds[]':
                                    params['commodityTypeIds[]'],
                            }}
                        />
                        <ContractFilter
                            name="billing-contract-filter"
                            containerRef={containerRef}
                            value={activeFilters.contract.map((id) =>
                                id.toString()
                            )}
                            defaultValue={DEFAULT_BILLING_FILTERING.contract.map(
                                (id) => id.toString()
                            )}
                            onChange={(value: string[]) => {
                                onChange(
                                    'contract',
                                    value.map((id) => parseInt(id))
                                )
                            }}
                            params={{
                                'nodeIds[]': params['nodeIds[]'],
                                'commodityTypeIds[]':
                                    params['commodityTypeIds[]'],
                                'providerIds[]': params['providerIds[]'],
                            }}
                        />
                        <AccountFilter
                            name="billing-account-filter"
                            containerRef={containerRef}
                            value={(activeFilters.account ?? []).map((id) =>
                                id.toString()
                            )}
                            defaultValue={(
                                DEFAULT_BILLING_FILTERING.account ?? []
                            ).map((id) => id.toString())}
                            onChange={(value: string[]) => {
                                onChange(
                                    'account',
                                    value.map((id) => parseInt(id))
                                )
                            }}
                            params={{
                                'nodeIds[]': params['nodeIds[]'],
                                'commodityTypeIds[]':
                                    params['commodityTypeIds[]'],
                                'providerIds[]': params['providerIds[]'],
                                'contractIds[]': params['contractIds[]'],
                            }}
                        />
                        <TagFilter
                            name="billing-tag-filter"
                            containerRef={containerRef}
                            value={activeFilters.tag.map((id) => id.toString())}
                            defaultValue={DEFAULT_BILLING_FILTERING.tag.map(
                                (id) => id.toString()
                            )}
                            onChange={(value: string[]) => {
                                onChange(
                                    'tag',
                                    value.map((id) => parseInt(id))
                                )
                            }}
                            params={{
                                'nodeIds[]': params['nodeIds[]'],
                                'commodityTypeIds[]':
                                    params['commodityTypeIds[]'],
                                'providerIds[]': params['providerIds[]'],
                                'contractIds[]': params['contractIds[]'],
                                'panIds[]': params['panIds[]'],
                            }}
                        />
                        <FacilityFilter
                            name="billing-facility-filter"
                            containerRef={containerRef}
                            value={activeFilters.facility.map((id) =>
                                id.toString()
                            )}
                            defaultValue={DEFAULT_BILLING_FILTERING.facility.map(
                                (id) => id.toString()
                            )}
                            onChange={(value: string[]) => {
                                onChange(
                                    'facility',
                                    value.map((id) => parseInt(id))
                                )
                            }}
                            params={{
                                'nodeIds[]': params['nodeIds[]'],
                                'commodityTypeIds[]':
                                    params['commodityTypeIds[]'],
                                'providerIds[]': params['providerIds[]'],
                                'contractIds[]': params['contractIds[]'],
                                'panIds[]': params['panIds[]'],
                                'tagIds[]': params['tagIds[]'],
                            }}
                        />
                        <USNFilter
                            name="billing-usn-filter"
                            containerRef={containerRef}
                            value={(activeFilters.usn ?? []).map((id) =>
                                id.toString()
                            )}
                            defaultValue={(
                                DEFAULT_BILLING_FILTERING.usn ?? []
                            ).map((id) => id.toString())}
                            onChange={(value: string[]) => {
                                onChange(
                                    'usn',
                                    value.map((id) => parseInt(id))
                                )
                            }}
                            params={{
                                'nodeIds[]': params['nodeIds[]'],
                                'commodityTypeIds[]':
                                    params['commodityTypeIds[]'],
                                'providerIds[]': params['providerIds[]'],
                                'contractIds[]': params['contractIds[]'],
                                'panIds[]': params['panIds[]'],
                                'tagIds[]': params['tagIds[]'],
                                'facilityIds[]': params['facilityIds[]'],
                            }}
                        />
                        <InvoiceNoFilter
                            name="billing-invoiceNo-filter"
                            containerRef={containerRef}
                            value={(activeFilters.invoiceNo ?? []).map((id) =>
                                id.toString()
                            )}
                            defaultValue={(
                                DEFAULT_BILLING_FILTERING.invoiceNo ?? []
                            ).map((id) => id.toString())}
                            onChange={(value: string[]) => {
                                onChange('invoiceNo', value)
                            }}
                            params={{
                                'nodeIds[]': params['nodeIds[]'],
                                'commodityTypeIds[]':
                                    params['commodityTypeIds[]'],
                                'providerIds[]': params['providerIds[]'],
                                'contractIds[]': params['contractIds[]'],
                                'panIds[]': params['panIds[]'],
                                'tagIds[]': params['tagIds[]'],
                                'facilityIds[]': params['facilityIds[]'],
                                'usnAccountIds[]': params['usnAccountIds[]'],
                                startDate: params['startDate'],
                                endDate: params['endDate'],
                            }}
                        />
                        <TimelineFilter
                            name="billing-timeline-filter"
                            value={activeFilters.timeline}
                            defaultValue={DEFAULT_BILLING_FILTERING.timeline}
                            onChange={(value: TimelineFilterValue) => {
                                onChange('timeline', value)
                            }}
                            containerRef={containerRef}
                        />
                    </>
                )}
                {showResetFiltersButton && (
                    <LinkButton
                        name="reset-filters"
                        iconBefore="sync"
                        label={t('filters.reset')}
                        onClick={handleReset}
                    />
                )}
                <FilterToggleButton
                    isVisible={isVisible}
                    onToggle={handleToggleVisibility}
                />
            </div>
        </div>
    )
}

export const BillingFilters = () => {
    const updateActiveFilter = useBillingFiltersStore(
        (state) => state.updateActiveFilter
    )

    const activeFilters = useBillingFiltersStore((state) => state.activeFilters)

    const selectHasDefaultFilters = createSelectHasDefaultFilters(
        DEFAULT_BILLING_FILTERING
    )
    const showResetFiltersButton = !useBillingFiltersStore(
        selectHasDefaultFilters
    )

    const handleResetActiveFilters = useBillingFiltersStore(
        (state) => state.resetActiveFilters
    )

    return (
        <BillingFiltersContainer
            onChange={updateActiveFilter}
            activeFilters={activeFilters}
            showResetFiltersButton={showResetFiltersButton}
            handleReset={handleResetActiveFilters}
        />
    )
}
